import { validationErrorChecker } from 'sonata-react-components';

import type { ZodSchema } from 'zod';

type StateWithErrors = {
  errors: Record<string, any>;
  hasAttemptedValidation: boolean;
};

/**
 * Re-runs validation for a single field and reconciles the result into
 * `state.errors[fieldKey]`.
 *
 * Form providers (Insertion Order, Line Item) cache validation results on
 * `state.errors` so the sidebar/toast/inline UI all read the same shape.
 * When the user mutates a field after their first save attempt, that
 * cached entry needs to either update (still invalid, possibly with a
 * different message) or disappear (now valid). Doing this in every
 * field setter inline led to copy/paste between providers — this helper
 * centralises the pattern.
 *
 * Behavior:
 * - No-op (returns `state` unchanged) when `hasAttemptedValidation` is
 *   false, so we don't surface errors before the user has asked to
 *   validate.
 * - Otherwise runs `validationErrorChecker` against the supplied state +
 *   validator, takes just the `fieldKey` slice of the result, and either
 *   assigns it onto a fresh errors object or `delete`s it.
 */
export function reconcileFieldError<T extends StateWithErrors>(state: T, fieldKey: string, validator: ZodSchema): T {
  if (!state.hasAttemptedValidation) {
    return state;
  }

  const fieldValidation = validationErrorChecker(state, validator)?.[fieldKey];
  const newErrors = { ...state.errors };
  if (fieldValidation) {
    newErrors[fieldKey] = fieldValidation;
  } else {
    delete newErrors[fieldKey];
  }

  return {
    ...state,
    errors: newErrors
  };
}
